import { distance as turfDistance } from '@turf/turf';
import { GraphhopperInstruction, JOIN_SOURCE, PROFILE_LINE, TurnInstruction, isJoinEntry } from '../turnInstructions';
import { SPUR_MAX_METERS, capturedScale, isSnapped, pathLength, turnsBackAtJoin } from './joinSpurs';
import { isTerminal, isUTurn, ManeuverSign } from './maneuverSign';

/**
 * GraphHopper's turns for a line we already have, by map-matching it.
 *
 * Reversing a route, or walking it back, mirrors each segment's turns rather than routing it
 * again, so the line stays the one the user drew. Mirroring gets most turns right but not a
 * fork: a "keep left" walked backwards is two paths merging. Matching the reversed line asks
 * GraphHopper to describe exactly that line, markers included.
 */

/** Our GraphHopper plan rejects a match request with more points than this. */
export const MAX_MATCH_POINTS = 500;
/** Pieces of a long run overlap by this much, so a turn near a cut is inside one of them. */
const OVERLAP_METERS = 80;
/** The match counts only if it drew our line. */
const ON_LINE_METERS = 3;
const LENGTH_TOLERANCE_RATIO = 0.01;
const LENGTH_TOLERANCE_METERS = 5;
/** A turn this close to a run's segment boundary is the turn at that marker. */
const AT_MARKER_METERS = 0.05;

export interface MatchSegment {
  coordinates: number[][];
  instructions: TurnInstruction[];
  profile?: string | null;
}

/** Consecutive segments matched as one line. */
export interface MatchRun {
  segments: number[];
  profile: string;
  line: number[][];
  /** Metres along `line` where each of `segments` starts, then where the last one ends. */
  starts: number[];
}

/** A part of a run sent in one request, and the stretch of the run it answers for. */
export interface MatchPiece {
  coordinates: number[][];
  offsetMeters: number;
  fromMeters: number;
  toMeters: number;
}

/** A turn GraphHopper reported, by metres along the run. */
export interface MatchedTurn {
  meters: number;
  sign: number;
  text: string;
  street?: string;
}

/**
 * The segments among `indices` whose turns were mirrored, grouped into runs that meet at markers
 * the route passes through. A bike or car segment was routed again rather than mirrored, a
 * straight line has no turns, and a turnaround is ours to announce, so each ends a run.
 */
export function matchRuns(segments: MatchSegment[], indices: number[]): MatchRun[] {
  const wanted = new Set(indices);
  const mirrored = (k: number) => {
    const segment = segments[k];
    const profile = segment?.profile;
    return wanted.has(k) && !!profile && ![PROFILE_LINE, 'bike', 'car'].includes(profile) && isSnapped(segment);
  };

  const runs: number[][] = [];
  segments.forEach((segment, k) => {
    if (!mirrored(k)) return;
    const current = runs[runs.length - 1];
    const previous = current?.[current.length - 1];
    const continues =
      previous === k - 1 &&
      segments[previous].profile === segment.profile &&
      !turnsBackAtJoin(segments[previous].coordinates, segment.coordinates);
    if (continues) current.push(k);
    else runs.push([k]);
  });

  return runs.map((run) => {
    const line: number[][] = [];
    const starts: number[] = [];
    let walked = 0;
    run.forEach((k) => {
      starts.push(walked);
      segments[k].coordinates.forEach((c) => {
        const last = line[line.length - 1];
        if (last && last[0] === c[0] && last[1] === c[1]) return;
        if (last) walked += meters(last, c);
        line.push([c[0], c[1]]);
      });
    });
    starts.push(walked);
    return { segments: run, profile: segments[run[0]].profile!, line, starts };
  });
}

/** A run cut into pieces of at most `maxPoints`, each overlapping the next by `OVERLAP_METERS`. */
export function matchPieces(run: MatchRun, maxPoints: number = MAX_MATCH_POINTS): MatchPiece[] {
  const along = cumulative(run.line);
  const total = along[along.length - 1];
  if (run.line.length <= maxPoints) return [{ coordinates: run.line, offsetMeters: 0, fromMeters: 0, toMeters: total }];

  const ranges: [number, number][] = [];
  let start = 0;
  for (;;) {
    const end = Math.min(start + maxPoints - 1, run.line.length - 1);
    ranges.push([start, end]);
    if (end === run.line.length - 1) break;
    let next = end;
    while (next > start + 1 && along[end] - along[next] < OVERLAP_METERS) next -= 1;
    start = next;
  }

  return ranges.map(([from, to], i) => ({
    coordinates: run.line.slice(from, to + 1),
    offsetMeters: along[from],
    // Each piece answers for its own stretch: the cuts fall in the middle of the overlaps.
    fromMeters: i === 0 ? 0 : (along[from] + along[ranges[i - 1][1]]) / 2,
    toMeters: i === ranges.length - 1 ? total : (along[ranges[i + 1][0]] + along[to]) / 2
  }));
}

/**
 * The turns a matched piece reports within its own stretch of the run, or null when the match is
 * not our line. GraphHopper's U-turns are left out: a turnaround is ours, and it misplaces them.
 */
export function turnsFromMatch(
  piece: MatchPiece,
  matched: { coordinates: number[][]; instructions?: GraphhopperInstruction[] }
): MatchedTurn[] | null {
  const coordinates = (matched.coordinates ?? []).filter(
    (c, i, all) => i === 0 || c[0] !== all[i - 1][0] || c[1] !== all[i - 1][1]
  );
  if (coordinates.length < 2) return null;

  const ours = cumulative(piece.coordinates);
  if (!follows(coordinates, piece.coordinates, ours) || !follows(piece.coordinates, coordinates, cumulative(coordinates))) {
    return null;
  }
  const planned = pathLength(piece.coordinates);
  if (Math.abs(pathLength(coordinates) - planned) > Math.max(LENGTH_TOLERANCE_METERS, planned * LENGTH_TOLERANCE_RATIO)) {
    return null;
  }

  const turns: MatchedTurn[] = [];
  (matched.instructions ?? []).forEach((instruction, index) => {
    const sign = instruction.sign ?? ManeuverSign.CONTINUE;
    // The first describes the piece's own start.
    if (index === 0 || isTerminal(sign) || isUTurn(sign)) return;
    const at = matched.coordinates[instruction.interval?.[0] ?? -1];
    if (!at) return;
    const metersAlong = piece.offsetMeters + project(piece.coordinates, ours, at, 0).along;
    if (metersAlong < piece.fromMeters || metersAlong >= piece.toMeters) return;

    const turn: MatchedTurn = { meters: metersAlong, sign, text: instruction.text ?? '' };
    const street = instruction.street_name?.trim();
    if (street) turn.street = street;
    turns.push(turn);
  });
  return turns;
}

/**
 * Each of the run's segments with its mirrored turns replaced by the matched ones, and every
 * marker inside the run settled. Anything a person wrote, re-signed or hid is kept, and a matched
 * turn beside it is not written: their choice wins.
 */
export function withMatchedTurns(segments: MatchSegment[], run: MatchRun, turns: MatchedTurn[]): TurnInstruction[][] {
  const persons: number[] = [];
  run.segments.forEach((k, i) => {
    const scale = capturedScale(segments[k]);
    segments[k].instructions.forEach((turn) => {
      if (isPersons(turn)) persons.push(run.starts[i] + turn.d / scale);
    });
  });
  const kept = turns.filter((turn) => persons.every((at) => Math.abs(at - turn.meters) > SPUR_MAX_METERS));

  return run.segments.map((k, i) => {
    const segment = segments[k];
    const scale = capturedScale(segment);
    const start = run.starts[i];
    const end = run.starts[i + 1];
    const last = i === run.segments.length - 1;
    const instructions = segment.instructions;
    const arrival = instructions.map((turn) => isTerminal(turn.sign)).lastIndexOf(true);

    // A turn on the marker after this segment belongs to it, as a split keeps it.
    const mine = kept.filter(
      (turn) => turn.meters >= start + (i === 0 ? 0 : AT_MARKER_METERS) && (last ? turn.meters <= end : turn.meters < end + AT_MARKER_METERS)
    );
    const written: TurnInstruction[] = mine.map((turn) => {
      const atEnd = !last && turn.meters >= end - AT_MARKER_METERS;
      const entry: TurnInstruction = {
        d: atEnd && arrival >= 0 ? instructions[arrival].d : round((turn.meters - start) * scale),
        sign: turn.sign,
        text: turn.text
      };
      if (turn.street) entry.street = turn.street;
      return entry;
    });

    const body = [
      ...instructions.filter((turn, index) => index !== 0 && !isTerminal(turn.sign) && isPersons(turn)),
      ...written
    ].sort((x, y) => x.d - y.d);
    const settle = (turn: TurnInstruction, isSettled: boolean): TurnInstruction => {
      const { src: _previous, ...rest } = turn;
      return isSettled ? { ...rest, src: JOIN_SOURCE } : rest;
    };
    const opening = instructions[0] ? [settle(instructions[0], i > 0)] : [];
    const ending = instructions.filter((turn, index) => index > 0 && isTerminal(turn.sign)).map((turn) => settle(turn, !last));
    return [...opening, ...body, ...ending];
  });
}

function isPersons(turn: TurnInstruction): boolean {
  if (isJoinEntry(turn)) return false;
  return turn.cue != null || !!turn.utext || turn.usign != null || !!turn.hidden;
}

/**
 * Whether every point of `path` lies within `ON_LINE_METERS` of `line`. Both run the same way, so
 * each point is looked for just past where the last one was found: linear, not every point
 * against every segment, which froze the planner for seconds on a long route.
 */
function follows(path: number[][], line: number[][], along: number[]): boolean {
  let hint = 0;
  for (const p of path) {
    const near = project(line, along, p, hint, SEARCH_SEGMENTS);
    const found = near.distance <= ON_LINE_METERS ? near : project(line, along, p, 0);
    if (found.distance > ON_LINE_METERS) return false;
    hint = found.index;
  }
  return true;
}

/** How many segments past the last match a point is looked for before searching the whole line. */
const SEARCH_SEGMENTS = 40;

/**
 * Where `p` falls on `line`, whose vertices lie `along` metres from its start, searching `limit`
 * segments from `hint`: the segment, the metres along the line to the nearest point, and how far
 * off it `p` is. A local flat projection, exact enough over a segment.
 */
function project(
  line: number[][],
  along: number[],
  p: number[],
  hint: number,
  limit = Infinity
): { index: number; along: number; distance: number } {
  const metersPerDegreeLat = 110_540;
  const metersPerDegreeLng = 111_320 * Math.cos((p[1] * Math.PI) / 180);
  let best = { index: hint, along: 0, distance: Infinity };
  for (let k = Math.max(hint, 0) + 1; k < line.length && k <= hint + limit; k += 1) {
    const start = line[k - 1];
    const end = line[k];
    const ax = (start[0] - p[0]) * metersPerDegreeLng;
    const ay = (start[1] - p[1]) * metersPerDegreeLat;
    const dx = (end[0] - start[0]) * metersPerDegreeLng;
    const dy = (end[1] - start[1]) * metersPerDegreeLat;
    const lengthSquared = dx * dx + dy * dy;
    const t = lengthSquared === 0 ? 0 : Math.min(1, Math.max(0, -(ax * dx + ay * dy) / lengthSquared));
    const distance = Math.hypot(ax + t * dx, ay + t * dy);
    if (distance < best.distance) best = { index: k - 1, along: along[k - 1] + t * (along[k] - along[k - 1]), distance };
  }
  return best;
}

function cumulative(line: number[][]): number[] {
  const along = [0];
  for (let i = 1; i < line.length; i += 1) along.push(along[i - 1] + meters(line[i - 1], line[i]));
  return along;
}

function meters(p: number[], q: number[]): number {
  return turfDistance([p[0], p[1]], [q[0], q[1]], { units: 'meters' });
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

interface LineStringLike {
  lineStringGeometry: { coordinates: number[][] };
  lineStringProperties: { id?: string | null; instructions?: TurnInstruction[]; profile?: string | null };
}

export function matchSegmentOf(lineString: LineStringLike): MatchSegment {
  return {
    coordinates: lineString.lineStringGeometry?.coordinates ?? [],
    instructions: lineString.lineStringProperties?.instructions ?? [],
    profile: lineString.lineStringProperties?.profile
  };
}

/**
 * Writes the matched turns into the run's line strings, if they are still the ones that were
 * matched: the same ids in the same places, along the same line. Returns whether it wrote.
 */
export function applyMatchedTurns(lineStrings: LineStringLike[], ids: (string | null | undefined)[], run: MatchRun, turns: MatchedTurn[]): boolean {
  const unchanged = run.segments.every((k, i) => !!ids[i] && lineStrings[k]?.lineStringProperties?.id === ids[i]);
  if (!unchanged) return false;
  const segments = lineStrings.map(matchSegmentOf);
  const [now] = matchRuns(segments, run.segments);
  if (!now || now.segments.join() !== run.segments.join() || JSON.stringify(now.line) !== JSON.stringify(run.line)) return false;

  withMatchedTurns(segments, run, turns).forEach((instructions, i) => {
    lineStrings[run.segments[i]].lineStringProperties.instructions = instructions;
  });
  return true;
}
